"use client";
import { useEffect, useState, useRef } from "react";
import {
  getStorage,
  ref,
  uploadBytes,
  getDownloadURL,
  deleteObject,
} from "firebase/storage";
import {
  getDatabase,
  ref as dbRef,
  set,
  get,
  child,
  remove,
} from "firebase/database";
import JSZip from "jszip";
import { saveAs } from "file-saver"; 

const PurchaseOrders = ({ selectedUser }) => {
  const storage = getStorage();
  const db = getDatabase();
  const userId = selectedUser?.uid;
  const fileInputRef = useRef(null);
  const [poFile, setPoFile] = useState(null);
  const [poList, setPoList] = useState([]);
  const [filter, setFilter] = useState("ALL");
  const [selectedPO, setSelectedPO] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [showExportModal, setShowExportModal] = useState(false);
  const [quarterPOs, setQuarterPOs] = useState([]);
  const [exporting, setExporting] = useState(false);
  const [progress, setProgress] = useState(0);
  /* ---------- Financial Quarter ---------- */
  const getFinancialQuarter = () => {
    const m = new Date().getMonth() + 1;
    if (m >= 4 && m <= 6) return { q: "Q1", r: "Apr–Jun" };
    if (m >= 7 && m <= 9) return { q: "Q2", r: "Jul–Sep" };
    if (m >= 10 && m <= 12) return { q: "Q3", r: "Oct–Dec" };
    return { q: "Q4", r: "Jan–Mar" };
  };
  const { q, r } = getFinancialQuarter();
  /* ---------- Date Filter ---------- */
  const applyFilter = (po) => {
    const poDate = new Date(po.uploadedAt);
    const now = new Date();
    if (filter === "WEEK") {
      return (now - poDate) / (1000 * 60 * 60 * 24) <= 7;
    }
    if (filter === "MONTH") {
      return (
        poDate.getMonth() === now.getMonth() &&
        poDate.getFullYear() === now.getFullYear()
      );
    }
    return true;
  };
  /* ---------- Get Current Quarter POs ---------- */
  const getQuarterPOs = () => {
    const now = new Date();
    const currentMonth = now.getMonth(); // 0-based
    let quarterStartMonth, quarterEndMonth;
    if (currentMonth >= 3 && currentMonth <= 5) {
      // Apr-Jun (Q1, months 3-5)
      quarterStartMonth = 3;
      quarterEndMonth = 5;
    } else if (currentMonth >= 6 && currentMonth <= 8) {
      // Jul-Sep (Q2, 6-8)
      quarterStartMonth = 6;
      quarterEndMonth = 8;
    } else if (currentMonth >= 9 && currentMonth <= 11) {
      // Oct-Dec (Q3, 9-11)
      quarterStartMonth = 9;
      quarterEndMonth = 11;
    } else {
      // Jan-Mar (Q4, 0-2)
      quarterStartMonth = 0;
      quarterEndMonth = 2;
    }
    return poList.filter((po) => {
      const poMonth = new Date(po.uploadedAt).getMonth();
      const poYear = new Date(po.uploadedAt).getFullYear();
      const nowYear = now.getFullYear();
      // Handle year wrap for Q4-Jan (simplified: assume uploads are recent, no cross-year for now)
      return (
        poMonth >= quarterStartMonth &&
        poMonth <= quarterEndMonth &&
        poYear === nowYear
      );
    });
  };
  /* ---------- Fetch POs ---------- */
  const fetchPOs = async () => {
    if (!userId) return;
    const snapshot = await get(child(dbRef(db), `ActivePO/${userId}`));
    if (!snapshot.exists()) {
      setPoList([]);
      return;
    }
    const data = snapshot.val();
    const list = Object.keys(data).map((key) => ({
      id: key,
      name: data[key].fileName,
      uploadedAt: data[key].uploadedAt,
      storagePath: data[key].storagePath,
    }));
    list.sort((a, b) => new Date(b.uploadedAt) - new Date(a.uploadedAt));
    setPoList(list);
  };
  useEffect(() => {
    fetchPOs();
  }, [userId]);
  /* ---------- Upload PO ---------- */
  const uploadPO = async () => {
    if (!poFile || uploading || !userId) return;

    const MAX_SIZE = 5 * 1024 * 1024; // 5 MB
    if (poFile.size > MAX_SIZE) {
      alert("File size exceeds 5 MB. Please upload a file below 5 MB.");
      return;
    }

    setUploading(true);
    try {
      const timestampKey = Date.now().toString();
      const uploadedAt = new Date().toISOString();
      const storagePath = `ActivePO/${userId}/${timestampKey}_${poFile.name}`;
      const fileRef = ref(storage, storagePath);
      await uploadBytes(fileRef, poFile);
      await set(dbRef(db, `ActivePO/${userId}/${timestampKey}`), {
        fileName: poFile.name,
        uploadedAt,
        storagePath,
      });
      // ✅ clear upload section
      setPoFile(null);
      if (fileInputRef.current) fileInputRef.current.value = "";
      fetchPOs();
    } catch (err) {
      console.error("Upload failed:", err);
    } finally {
      setUploading(false);
    }
  };
  /* ---------- Open Export Modal ---------- */
  const openExportModal = () => {
    const qPOs = getQuarterPOs();
    setQuarterPOs(qPOs);
    if (qPOs.length === 0) {
      alert("No Purchase Orders found for current quarter.");
      return;
    }
    setShowExportModal(true);
  };
  /* ---------- EXPORT REAL PDFs AS ZIP ---------- */
  const exportPOsAsZip = async () => {
    if (exporting || quarterPOs.length === 0) return;
    setExporting(true);
    setProgress(0);
    try {
      const zip = new JSZip();
      const folder = zip.folder("Purchase_Orders");
      for (let i = 0; i < quarterPOs.length; i++) {
        const po = quarterPOs[i];
        const url = await getDownloadURL(ref(storage, po.storagePath));
        const res = await fetch(url);
        const blob = await res.blob();
        folder.file(po.name, blob);
        setProgress(((i + 1) / quarterPOs.length) * 80); // 80% for files, 20% for zip
      }
      const zipBlob = await zip.generateAsync({ type: "blob" });
      setProgress(100);
      // FIX: Use file-saver for reliable download in Safari/other browsers
      saveAs(
        zipBlob,
        `POs_${q}_${r}_${new Date().toLocaleDateString("en-GB")}.zip`
      );
      alert("Export completed! Check your Downloads folder."); // FIX: Add confirmation alert
      console.log("ZIP exported successfully"); // For debugging
    } catch (err) {
      console.error("Export failed:", err);
      alert("Export failed. Check console for details.");
    } finally {
      setExporting(false);
      setProgress(0);
      setShowExportModal(false);
    }
  };
  const handleCancelExport = () => {
    setShowExportModal(false);
    setExporting(false);
    setProgress(0);
  };

  /* ---------- Delete Selected PO ---------- */

  const deleteSelectedPO = async () => {
    if (!selectedPO || !userId) return;

    const confirmDelete = window.confirm(
      `Are you sure you want to delete "${selectedPO.name}"?`
    );
    if (!confirmDelete) return;

    try {
      // 1️⃣ Delete from Firebase Storage
      const fileRef = ref(storage, selectedPO.storagePath);
      await deleteObject(fileRef);

      // 2️⃣ Delete from Realtime Database
      const poDbRef = dbRef(db, `ActivePO/${userId}/${selectedPO.id}`);
      await remove(poDbRef);

      // 3️⃣ Reset UI state
      setSelectedPO(null);
      fetchPOs();

      alert("Purchase Order deleted successfully.");
    } catch (error) {
      console.error("Failed to delete PO:", error);
      alert("Failed to delete PO. Check console for details.");
    }
  };

  return (
    <div className="po-details-container">
      {/* LEFT PANEL */}
      <div className="left-sidebar">
        <h3>Purchase Orders</h3>
        <select value={filter} onChange={(e) => setFilter(e.target.value)}>
          <option value="ALL">All</option>
          <option value="WEEK">This Week</option>
          <option value="MONTH">This Month</option>
        </select>
        <div className="quarter-info">
          Current Financial Quarter: <b>{q}</b> ({r})
        </div>
        <div className="po-list">
          {poList.filter(applyFilter).map((po) => (
            <div
              key={po.id}
              className={`po-item ${selectedPO?.id === po.id ? "active" : ""}`}
              onClick={async () => {
                const url = await getDownloadURL(ref(storage, po.storagePath));
                setSelectedPO({ ...po, url });
              }}
            >
              <div className="po-name">{po.name}</div>
              <div className="po-date">
                {new Date(po.uploadedAt).toLocaleDateString("en-GB", {
                  day: "2-digit",
                  month: "short",
                  year: "numeric",
                })}
              </div>
            </div>
          ))}
        </div>
        <button className="export-btn" onClick={openExportModal}>
          Export Quarter POs
        </button>
      </div>

      {/* RIGHT PANEL */}

      <div className="right-panel">
        <div className="upload-box">
          <input
            ref={fileInputRef}
            type="file"
            accept="application/pdf"
            onChange={(e) => {
              const file = e.target.files[0];
              if (!file) return;

              const MAX_SIZE = 5 * 1024 * 1024; // 5 MB

              if (file.size > MAX_SIZE) {
                alert(
                  "File size exceeds 5 MB. Please upload a file below 5 MB."
                );
                e.target.value = "";
                setPoFile(null);
                return;
              }

              setPoFile(file);
            }}
          />
          <button onClick={uploadPO} disabled={uploading}>
            {uploading ? "Uploading..." : "Upload PO"}
          </button>
        </div>
        {selectedPO && (
          <button className="delete-btn" onClick={deleteSelectedPO}>
            Delete Selected PO
          </button>
        )}
        <div className="preview-box">
          {selectedPO ? (
            <iframe src={selectedPO.url} title="PO Preview" />
          ) : (
            <p>Select a PO to preview</p>
          )}
        </div>
      </div>

      {/* EXPORT MODAL */}
      {showExportModal && (
        <div className="modal-overlay">
          <div className="export-modal">
            <button className="modal-close" onClick={handleCancelExport}>
              ×
            </button>
            <div className="modal-header">
              <h3>
                Export POs for {q} ({r})
              </h3>
              <p>{quarterPOs.length} files selected</p>
            </div>
            <div className="modal-body">
              {!exporting ? (
                <div className="po-list-modal">
                  {quarterPOs.map((po) => (
                    <div key={po.id} className="po-item-modal">
                      <div className="po-name">{po.name}</div>
                      <div className="po-date">
                        {new Date(po.uploadedAt).toLocaleDateString("en-GB", {
                          day: "2-digit",
                          month: "short",
                          year: "numeric",
                        })}
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="progress-container">
                  <div className="progress-bar">
                    <div
                      className="progress-fill"
                      style={{ width: `${progress}%` }}
                    ></div>
                  </div>
                  <p>Preparing ZIP... {Math.round(progress)}%</p>
                </div>
              )}
            </div>
            <div className="modal-footer">
              {!exporting ? (
                <>
                  <button className="cancel-btn" onClick={handleCancelExport}>
                    Cancel
                  </button>
                  <button className="confirm-btn" onClick={exportPOsAsZip}>
                    Confirm Export
                  </button>
                </>
              ) : (
                <p>Do not close this window...</p>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
export default PurchaseOrders;
